import { useCallback, useEffect, useRef, useState } from "react";
import { useJson, getJson, ago, card, control, sectionLabel, TYPE_LABEL } from "./kb/shared";
import { WidgetCard, RANGES, queryUrl, autoTitle } from "./dash/Widget";
import { WidgetEditor } from "./dash/WidgetEditor";
import { DrillDrawer } from "./dash/Drilldown";

// Dashboards: user-built grids of widgets over the Knowledge Base data.
// A dashboard has a date range and a "focus" (threat actors/malware, countries,
// sectors) that narrows every widget it applies to. Saved in this browser
// (localStorage); Share puts the whole dashboard in a link; Export downloads it
// as a file, the data as CSV, or prints it.
// Routes: #/dash · #/dash/<id> · #/dash/shared/<encoded dashboard>

const STORAGE_KEY = "idlehour.dashboards.v1";
const REFRESH_MS = 5 * 60 * 1000;
const FILE_FORMAT = "idlehour-dashboard";
const EMPTY_FOCUS = { threats: [], countries: [], sectors: [] };

export function parseDashHash(hash = window.location.hash) {
  const parts = hash.replace(/^#\/?/, "").split("/").filter(Boolean);
  if (parts[0] !== "dash") return null;
  if (parts[1] === "shared" && parts[2]) return { view: "shared", data: parts.slice(2).join("/") };
  if (parts[1]) return { view: "dashboard", id: decodeURIComponent(parts[1]) };
  return { view: "list" };
}

const newId = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `d${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`);

function loadAll() {
  try {
    const list = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "[]");
    return Array.isArray(list) ? list : [];
  } catch {
    return [];
  }
}
function saveAll(list) {
  try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list)); return true; } catch { return false; }
}

// Keep only what a dashboard is made of; drop anything unexpected from files/links.
function sanitize(d) {
  if (!d || !Array.isArray(d.widgets)) return null;
  const chips = (list) => (Array.isArray(list) ? list : []).filter(x => x && x.id).slice(0, 20).map(x => ({ id: String(x.id), label: String(x.label || x.id).slice(0, 80), ...(x.type ? { type: String(x.type) } : {}) }));
  const focus = d.focus || {};
  return {
    name: String(d.name || "Untitled dashboard").slice(0, 120),
    range: RANGES.some(r => r.k === d.range) ? d.range : "30d",
    focus: { threats: chips(focus.threats), countries: chips(focus.countries), sectors: chips(focus.sectors) },
    widgets: d.widgets.slice(0, 40).filter(w => w && w.dataset).map(w => ({
      dataset: String(w.dataset), measure: w.measure ? String(w.measure) : "count", groupBy: w.groupBy ? String(w.groupBy) : "",
      chart: String(w.chart || "stat"), limit: Number(w.limit) || 10, width: [4, 6, 8, 12].includes(Number(w.width)) ? Number(w.width) : 6,
      title: String(w.title || "").slice(0, 120), ignoreFocus: !!w.ignoreFocus,
      filters: (Array.isArray(w.filters) ? w.filters : []).slice(0, 10).map(f => ({ dim: String(f.dim), values: (f.values || []).map(String).slice(0, 50), exclude: !!f.exclude, labels: f.labels && typeof f.labels === "object" ? f.labels : {} })),
      id: newId(),
    })),
  };
}

// URL-safe base64 of the dashboard JSON (unicode-safe).
function encodeDashboard(d) {
  const json = JSON.stringify({ name: d.name, range: d.range, focus: d.focus, widgets: d.widgets.map(({ id, ...w }) => w) });
  const bytes = new TextEncoder().encode(json);
  let bin = "";
  bytes.forEach(b => { bin += String.fromCharCode(b); });
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function decodeDashboard(s) {
  try {
    const bin = atob(s.replace(/-/g, "+").replace(/_/g, "/"));
    const d = sanitize(JSON.parse(new TextDecoder().decode(Uint8Array.from(bin, c => c.charCodeAt(0)))));
    return d ? { ...d, id: "shared" } : null;
  } catch {
    return null;
  }
}

function download(filename, text, type) {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = Object.assign(document.createElement("a"), { href: url, download: filename });
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
const fileSlug = (name) => String(name || "dashboard").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "dashboard";
const csvCell = (v) => {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// ─── Templates ───
const W = (o) => ({ title: "", filters: [], limit: 10, ...o });
const T = (id, label, type) => ({ id, label, type });
const TEMPLATES = [
  {
    key: "ransomware", name: "Ransomware landscape", tag: "Ransomware", blurb: "Claimed victims over time, where they are, which sectors, and which groups are most active.", range: "1y",
    widgets: [
      W({ dataset: "victims", groupBy: "", chart: "stat", width: 4, title: "Claimed victims" }),
      W({ dataset: "victims", groupBy: "month", chart: "column", width: 8, title: "Claimed victims per month" }),
      W({ dataset: "victims", groupBy: "country", chart: "map", width: 8, title: "Where victims are" }),
      W({ dataset: "victims", groupBy: "group", chart: "bar", width: 4, title: "Most active groups" }),
      W({ dataset: "victims", groupBy: "sector", chart: "bar", width: 6, title: "Sectors hit" }),
      W({ dataset: "victims", groupBy: "country", chart: "bar", width: 6, title: "Top victim countries" }),
    ],
  },
  {
    key: "showdown", name: "Ransomware showdown", tag: "Ransomware", blurb: "Akira, Qilin, LockBit 3.0 and Play head to head — swap in any groups you care about.", range: "1y",
    focus: { threats: [T("G1024", "Akira", "intrusion-set"), T("S1242", "Qilin", "malware"), T("S1202", "LockBit 3.0", "malware"), T("G1040", "Play", "intrusion-set")] },
    widgets: [
      W({ dataset: "victims", groupBy: "group", chart: "bar", width: 4, title: "Claimed victims by group" }),
      W({ dataset: "victims", groupBy: "month", chart: "column", width: 8, title: "Their claims per month (combined)" }),
      W({ dataset: "victims", groupBy: "country", chart: "map", width: 8, title: "Where their victims are" }),
      W({ dataset: "victims", groupBy: "sector", chart: "bar", width: 4, title: "Sectors they hit" }),
      W({ dataset: "techniqueUsage", measure: "users", groupBy: "parentTechnique", chart: "matrix", width: 12, title: "Their techniques (ATT&CK)" }),
    ],
  },
  {
    key: "actors", name: "Threat actor watchlist", tag: "Threat actors", blurb: "Track a set of actors: their combined ATT&CK coverage, their tools, and live indicators of those tools.", range: "30d",
    focus: { threats: [T("G0016", "APT29", "intrusion-set"), T("G0007", "APT28", "intrusion-set"), T("G0032", "Lazarus Group", "intrusion-set"), T("G1017", "Volt Typhoon", "intrusion-set")] },
    widgets: [
      W({ dataset: "techniqueUsage", measure: "groups", groupBy: "parentTechnique", chart: "matrix", width: 12, title: "Combined technique coverage" }),
      W({ dataset: "softwareUsage", measure: "groups", groupBy: "software", chart: "bar", width: 6, limit: 15, title: "Software they use" }),
      W({ dataset: "indicators", groupBy: "family", chart: "bar", width: 6, title: "Live indicators of their tools" }),
      W({ dataset: "indicators", groupBy: "day", chart: "column", width: 12, title: "New indicators of their tools per day" }),
    ],
  },
  {
    key: "malware", name: "Malware tracker", tag: "Malware", blurb: "Follow specific malware families: fresh C2s and payloads, what they do in ATT&CK, and who uses them.", range: "30d",
    focus: { threats: [T("S0154", "Cobalt Strike", "malware"), T("S1087", "AsyncRAT", "tool"), T("S0332", "Remcos", "tool")] },
    widgets: [
      W({ dataset: "indicators", groupBy: "", chart: "stat", width: 4, title: "C2 servers", filters: [{ dim: "threatType", values: ["botnet_cc"], exclude: false, labels: { botnet_cc: "Botnet C2" } }] }),
      W({ dataset: "indicators", groupBy: "family", chart: "bar", width: 4, title: "Indicators by family" }),
      W({ dataset: "indicators", groupBy: "type", chart: "bar", width: 4, title: "Indicator types" }),
      W({ dataset: "indicators", groupBy: "day", chart: "column", width: 12, title: "New indicators per day" }),
      W({ dataset: "techniqueUsage", measure: "users", groupBy: "parentTechnique", chart: "matrix", width: 12, title: "What they do (ATT&CK)" }),
      W({ dataset: "softwareUsage", measure: "groups", groupBy: "group", chart: "bar", width: 6, limit: 15, title: "Groups known to use them" }),
    ],
  },
  {
    key: "country", name: "Country exposure", tag: "Victims", blurb: "Everything aimed at one country — swap the focus for your own: ransomware claims, sectors, groups.", range: "1y",
    focus: { countries: [T("US", "United States")] },
    widgets: [
      W({ dataset: "victims", groupBy: "", chart: "stat", width: 4, title: "Claimed victims" }),
      W({ dataset: "victims", groupBy: "month", chart: "column", width: 8, title: "Claims per month" }),
      W({ dataset: "victims", groupBy: "sector", chart: "bar", width: 6, title: "Sectors hit" }),
      W({ dataset: "victims", groupBy: "group", chart: "bar", width: 6, title: "Most active groups" }),
      W({ dataset: "groups", groupBy: "origin", chart: "bar", width: 6, title: "Where groups that target it come from" }),
    ],
  },
  {
    key: "sector", name: "Sector watch", tag: "Victims", blurb: "One industry's ransomware exposure — who's hitting it, where, and how often.", range: "1y",
    focus: { sectors: [T("Healthcare", "Healthcare")] },
    widgets: [
      W({ dataset: "victims", groupBy: "", chart: "stat", width: 4, title: "Claimed victims" }),
      W({ dataset: "victims", groupBy: "month", chart: "column", width: 8, title: "Claims per month" }),
      W({ dataset: "victims", groupBy: "country", chart: "map", width: 8, title: "Where" }),
      W({ dataset: "victims", groupBy: "group", chart: "bar", width: 4, title: "Who" }),
    ],
  },
  {
    key: "indicators", name: "Indicator activity", tag: "Indicators", blurb: "What's in the abuse.ch feeds right now: malware families, threat types, and how confident we are in them.", range: "30d",
    widgets: [
      W({ dataset: "indicators", groupBy: "", chart: "stat", width: 4, title: "New indicators" }),
      W({ dataset: "indicators", groupBy: "", chart: "stat", width: 4, title: "New C2 servers", filters: [{ dim: "threatType", values: ["botnet_cc"], exclude: false, labels: { botnet_cc: "Botnet C2" } }] }),
      W({ dataset: "indicators", groupBy: "confidence", chart: "bar", width: 4, title: "By confidence" }),
      W({ dataset: "indicators", groupBy: "day", chart: "column", width: 12, title: "New indicators per day" }),
      W({ dataset: "indicators", groupBy: "family", chart: "bar", width: 6, limit: 15, title: "Top malware families" }),
      W({ dataset: "indicators", groupBy: "threatType", chart: "bar", width: 6, title: "Threat types" }),
    ],
  },
  {
    key: "heatmap", name: "ATT&CK heatmap", tag: "ATT&CK", blurb: "Which techniques threat groups lean on most, laid out as the ATT&CK matrix. Add actors to the focus to see just theirs.", range: "all",
    widgets: [
      W({ dataset: "techniqueUsage", measure: "groups", groupBy: "parentTechnique", chart: "matrix", width: 12, title: "Techniques used by threat groups" }),
      W({ dataset: "techniqueUsage", measure: "groups", groupBy: "tactic", chart: "bar", width: 6, limit: 15, title: "Groups per tactic" }),
      W({ dataset: "softwareUsage", measure: "groups", groupBy: "software", chart: "bar", width: 6, limit: 15, title: "Most used software" }),
    ],
  },
  {
    key: "attack", name: "ATT&CK landscape", tag: "ATT&CK", blurb: "Most-used techniques and tools, and where threat groups come from.", range: "all",
    widgets: [
      W({ dataset: "techniqueUsage", measure: "groups", groupBy: "technique", chart: "bar", width: 6, limit: 15, title: "Most used techniques" }),
      W({ dataset: "softwareUsage", measure: "groups", groupBy: "software", chart: "bar", width: 6, limit: 15, title: "Most used software" }),
      W({ dataset: "groups", groupBy: "origin", chart: "map", width: 8, title: "Attributed origin of threat groups" }),
      W({ dataset: "groups", groupBy: "source", chart: "bar", width: 4, title: "Groups by source" }),
    ],
  },
];

// ─── Focus bar: "add threat actor / malware / country / sector" ───
function Picker({ placeholder, search, onPick, ui }) {
  const { B, fs, TypeIcon, getConf } = ui;
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    let live = true;
    const t = setTimeout(() => search(q).then(r => live && setItems(r)).catch(() => live && setItems([])), 150);
    return () => { live = false; clearTimeout(t); };
  }, [q, open, search]);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <input value={q} onChange={e => { setQ(e.target.value); setOpen(true); }} onFocus={() => setOpen(true)} placeholder={placeholder}
        style={{ ...control(ui), width: 210, borderRadius: 20, padding: "6px 12px" }} />
      {open && items.length > 0 && (
        <div style={{ position: "absolute", top: "calc(100% + 4px)", left: 0, minWidth: 260, zIndex: 30, background: B.bgPanel, border: `1px solid ${B.borderLight}`, borderRadius: 8, boxShadow: "0 12px 32px rgba(0,0,0,0.35)", maxHeight: 300, overflowY: "auto" }}>
          {items.map(it => (
            <div key={it.id} onMouseDown={() => { onPick(it); setQ(""); setOpen(false); }} style={{ display: "flex", gap: 8, alignItems: "center", padding: "7px 12px", cursor: "pointer", fontSize: fs(12), color: B.text }}>
              {it.type && <TypeIcon type={it.type} color={getConf(it.type).color} size={13} />}
              <span style={{ flex: 1 }}>{it.label}</span>
              {it.hint && <span style={{ color: B.textDim, fontSize: fs(10) }}>{it.hint}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const searchThreats = (q) => (q.trim()
  ? getJson(`/api/kb/search?q=${encodeURIComponent(q.trim())}`).then(d => (d.items || [])
    .filter(s => ["intrusion-set", "malware", "tool"].includes(s.type))
    .slice(0, 10).map(s => ({ id: s.attackId || s.id, label: s.name, type: s.type, hint: TYPE_LABEL[s.type] })))
  : Promise.resolve([]));
const searchValues = (dim) => (q) => getJson(`/api/dash/values?dataset=victims&dim=${dim}&q=${encodeURIComponent(q)}`)
  .then(d => (d.values || []).slice(0, 12).map(v => ({ id: String(v.key), label: v.label, hint: `${v.count.toLocaleString()} victims` })));
const searchCountries = searchValues("country");
const searchSectors = searchValues("sector");

function FocusBar({ focus, onChange, ui }) {
  const { B, fs, TypeIcon, getConf } = ui;
  const f = { ...EMPTY_FOCUS, ...(focus || {}) };
  const add = (kind) => (item) => { if (!f[kind].some(x => x.id === item.id)) onChange({ ...f, [kind]: [...f[kind], { id: item.id, label: item.label, ...(item.type ? { type: item.type } : {}) }] }); };
  const remove = (kind, id) => onChange({ ...f, [kind]: f[kind].filter(x => x.id !== id) });
  const chip = (kind, x) => (
    <span key={kind + x.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: fs(12), color: B.text, background: B.primary + "14", border: `1px solid ${B.primary}44`, borderRadius: 20, padding: "4px 4px 4px 10px" }}>
      {x.type && <TypeIcon type={x.type} color={getConf(x.type).color} size={12} />}
      {kind === "countries" && <span style={{ color: B.textDim, fontSize: fs(10) }}>Country</span>}
      {kind === "sectors" && <span style={{ color: B.textDim, fontSize: fs(10) }}>Sector</span>}
      {x.label}
      <button onClick={() => remove(kind, x.id)} aria-label={`Remove ${x.label} from focus`} style={{ background: "none", border: "none", color: B.textDim, cursor: "pointer", fontSize: fs(14), padding: "0 5px", lineHeight: 1 }}>×</button>
    </span>
  );
  const any = f.threats.length + f.countries.length + f.sectors.length > 0;
  return (
    <div style={{ ...card(ui), padding: "10px 12px", marginBottom: 14, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
      <span style={{ fontSize: fs(11), color: B.textDim, fontWeight: 700, textTransform: "uppercase", letterSpacing: 1.2, marginRight: 4 }}>Focus</span>
      {!any && <span style={{ fontSize: fs(12), color: B.textDim }}>Everything — add threat actors, malware, countries or sectors to narrow every widget.</span>}
      {f.threats.map(x => chip("threats", x))}
      {f.countries.map(x => chip("countries", x))}
      {f.sectors.map(x => chip("sectors", x))}
      <span style={{ display: "flex", gap: 6, flexWrap: "wrap", marginLeft: any ? 6 : 0 }}>
        <Picker placeholder="+ Threat actor or malware" search={searchThreats} onPick={add("threats")} ui={ui} />
        <Picker placeholder="+ Country" search={searchCountries} onPick={add("countries")} ui={ui} />
        <Picker placeholder="+ Sector" search={searchSectors} onPick={add("sectors")} ui={ui} />
      </span>
      {any && <button onClick={() => onChange(EMPTY_FOCUS)} style={{ marginLeft: "auto", background: "none", border: "none", color: B.accent, cursor: "pointer", fontSize: fs(12), fontFamily: "'Satoshi', sans-serif" }}>Clear focus</button>}
    </div>
  );
}

// ─── Export menu ───
function ExportMenu({ dash, schema, ui, onStatus }) {
  const { B, fs, HP } = ui;
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const exportJson = () => {
    const { id, updatedAt, ...rest } = dash;
    const body = { format: FILE_FORMAT, version: 1, exportedAt: new Date().toISOString(), ...rest, widgets: rest.widgets.map(({ id: _id, ...w }) => w) };
    download(`${fileSlug(dash.name)}.dashboard.json`, JSON.stringify(body, null, 2), "application/json");
    onStatus("Dashboard file downloaded — import it from the Dashboards page to restore or share it.");
  };

  const exportCsv = async () => {
    setBusy(true);
    try {
      const focusText = [...(dash.focus?.threats || []), ...(dash.focus?.countries || []), ...(dash.focus?.sectors || [])].map(x => x.label).join("; ");
      const lines = [[`Dashboard`, dash.name], [`Exported`, new Date().toISOString()], [`Date range`, RANGES.find(r => r.k === dash.range)?.l || dash.range], ...(focusText ? [[`Focus`, focusText]] : []), []];
      for (const w of dash.widgets) {
        const d = await getJson(queryUrl(w, dash.range, dash.focus), { fresh: true });
        lines.push([w.title || autoTitle(w, schema)]);
        lines.push([`Source`, `${d.datasetLabel} (${d.source})`]);
        if (!d.groupBy) {
          lines.push([d.measureLabel], [d.total]);
        } else {
          lines.push([d.groupByLabel, d.measureLabel]);
          for (const r of d.rows) lines.push([r.label, r.value]);
          if (d.other) lines.push(["Everything else", d.other]);
        }
        lines.push([]);
      }
      download(`${fileSlug(dash.name)}.csv`, lines.map(l => l.map(csvCell).join(",")).join("\r\n"), "text/csv");
      onStatus("Data for every widget downloaded as one CSV.");
    } catch (e) {
      onStatus(`Export failed: ${e.message}`);
    }
    setBusy(false);
  };

  const item = { display: "block", width: "100%", textAlign: "left", background: "none", border: "none", padding: "9px 14px", cursor: "pointer", fontSize: fs(12), color: B.text, fontFamily: "'Satoshi', sans-serif" };
  const hint = { display: "block", fontSize: fs(11), color: B.textDim, marginTop: 2 };
  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button onClick={() => setOpen(o => !o)} disabled={busy} style={{ ...HP.navBtn, padding: "8px 14px" }}>{busy ? "Exporting…" : "Export ▾"}</button>
      {open && (
        <div style={{ position: "absolute", right: 0, top: "calc(100% + 6px)", zIndex: 30, width: 270, background: B.bgPanel, border: `1px solid ${B.borderLight}`, borderRadius: 10, boxShadow: "0 14px 36px rgba(0,0,0,0.4)", overflow: "hidden" }}>
          <button style={item} onClick={() => { setOpen(false); exportJson(); }}>Dashboard file (.json)<span style={hint}>Re-import it later, or send it to someone</span></button>
          <button style={item} onClick={() => { setOpen(false); exportCsv(); }}>Data (.csv)<span style={hint}>Every widget's numbers in one spreadsheet</span></button>
          <button style={item} onClick={() => { setOpen(false); setTimeout(() => window.print(), 50); }}>Print or save as PDF<span style={hint}>Uses your browser's print dialog</span></button>
        </div>
      )}
    </div>
  );
}

// Print: only the dashboard, full height, colours kept.
const printCss = (bg) => `
@media print {
  @page { margin: 10mm; }
  html, body, #root { height: auto !important; overflow: visible !important; background: ${bg} !important; }
  body * { visibility: hidden; }
  #dash-print, #dash-print * { visibility: visible; }
  #dash-print { position: absolute; left: 0; top: 0; width: 100%; background: ${bg}; padding: 4mm; box-sizing: border-box; }
  #dash-print [data-noprint] { display: none !important; }
  #dash-print .print-only { display: block !important; margin-bottom: 10px; }
  #dash-print > * { break-inside: avoid; }
  * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
  [style*="height: 100vh"] { height: auto !important; overflow: visible !important; }
  [style*="overflow-y: auto"], [style*="overflow: hidden"] { overflow: visible !important; }
}`;

// ─── Pieces ───
function DashboardGrid({ dash, schema, ui, editing, refreshKey, onChange, onEditWidget, onDrill }) {
  const ref = useRef(null);
  const [wide, setWide] = useState(true);
  useEffect(() => {
    if (!ref.current) return;
    const ro = new ResizeObserver(([e]) => setWide(e.contentRect.width >= 760));
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);
  const move = (i, dir) => {
    const ws = [...dash.widgets];
    const j = i + dir;
    if (j < 0 || j >= ws.length) return;
    [ws[i], ws[j]] = [ws[j], ws[i]];
    onChange({ ...dash, widgets: ws });
  };
  return (
    <div ref={ref} style={{ display: "grid", gridTemplateColumns: wide ? "repeat(12, minmax(0, 1fr))" : "minmax(0, 1fr)", gap: 14, alignItems: "stretch" }}>
      {dash.widgets.map((w, i) => (
        <div key={w.id} style={{ gridColumn: wide ? `span ${w.width || 6}` : "auto", minWidth: 0, breakInside: "avoid" }}>
          <WidgetCard w={w} range={dash.range} focus={dash.focus} schema={schema} ui={ui} refreshKey={refreshKey} editing={editing}
            isFirst={i === 0} isLast={i === dash.widgets.length - 1}
            onMove={(dir) => move(i, dir)} onEdit={() => onEditWidget(w)} onDrill={editing ? null : onDrill}
            onRemove={() => onChange({ ...dash, widgets: dash.widgets.filter(x => x.id !== w.id) })} />
        </div>
      ))}
    </div>
  );
}

function DashboardView({ dash, schema, ui, readOnly, onChange, onDelete, onDuplicate, onSaveCopy }) {
  const { B, fs, HP } = ui;
  const [editing, setEditing] = useState(false);
  const [editor, setEditor] = useState(null); // null | "new" | widget
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastRefresh, setLastRefresh] = useState(new Date().toISOString());
  const [status, setStatus] = useState("");
  const [drill, setDrill] = useState(null);

  // Re-query every few minutes while the tab is visible, so widgets follow the data.
  useEffect(() => {
    const tick = () => { if (document.visibilityState === "visible") { setRefreshKey(k => k + 1); setLastRefresh(new Date().toISOString()); } };
    const id = setInterval(tick, REFRESH_MS);
    return () => clearInterval(id);
  }, []);

  const flash = (msg) => { setStatus(msg); setTimeout(() => setStatus(s => (s === msg ? "" : s)), 6000); };
  const share = async () => {
    const url = `${window.location.origin}${window.location.pathname}#/dash/shared/${encodeDashboard(dash)}`;
    try { await navigator.clipboard.writeText(url); flash("Share link copied — anyone with it can open this dashboard."); }
    catch { setStatus(url); }
  };

  const saveWidget = (w) => {
    if (editor === "new") onChange({ ...dash, widgets: [...dash.widgets, { ...w, id: newId() }] });
    else onChange({ ...dash, widgets: dash.widgets.map(x => (x.id === editor.id ? { ...w, id: x.id } : x)) });
    setEditor(null);
  };

  const pill = (on) => ({ ...control(ui), cursor: "pointer", borderRadius: 20, padding: "6px 13px", fontWeight: on ? 700 : 500, color: on ? B.text : B.textMuted, background: on ? B.primary + "18" : B.bgCard, borderColor: on ? B.primary + "55" : B.border });
  const focusText = [...(dash.focus?.threats || []), ...(dash.focus?.countries || []), ...(dash.focus?.sectors || [])].map(x => x.label).join(", ");

  return (
    <div id="dash-print">
      <div data-noprint style={{ fontSize: fs(12), color: B.textDim, marginBottom: 10 }}><a href="#/dash" style={{ color: B.textDim, textDecoration: "none" }}>Dashboards</a> / {dash.name}</div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        {editing ? (
          <input value={dash.name} onChange={e => onChange({ ...dash, name: e.target.value })} aria-label="Dashboard name" style={{ ...control(ui), fontSize: fs(22), fontWeight: 900, flex: "1 1 300px", padding: "6px 10px" }} />
        ) : (
          <h1 style={{ fontSize: fs(26), fontWeight: 900, margin: 0, color: B.text, flex: "1 1 300px" }}>{dash.name}</h1>
        )}
        <span data-noprint style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {readOnly ? (
            <>
              <ExportMenu dash={dash} schema={schema} ui={ui} onStatus={flash} />
              <button onClick={onSaveCopy} style={{ ...HP.primaryBtn, padding: "9px 16px" }}>Save a copy</button>
            </>
          ) : (
            <>
              {editing && <button onClick={() => setEditor("new")} style={{ ...HP.primaryBtn, padding: "9px 16px" }}>+ Add widget</button>}
              <button onClick={() => setEditing(e => !e)} style={{ ...HP.navBtn, padding: "8px 14px" }}>{editing ? "Done" : "Edit"}</button>
              <button onClick={share} style={{ ...HP.navBtn, padding: "8px 14px" }}>Share</button>
              <ExportMenu dash={dash} schema={schema} ui={ui} onStatus={flash} />
              {editing && <button onClick={onDuplicate} style={{ ...HP.navBtn, padding: "8px 14px" }}>Duplicate</button>}
              {editing && <button onClick={() => { if (window.confirm(`Delete "${dash.name}"? This can't be undone.`)) onDelete(); }} style={{ ...HP.navBtn, padding: "8px 14px", color: "#dc2626" }}>Delete</button>}
            </>
          )}
        </span>
      </div>

      {/* Printed copies say what they show, since the controls aren't printed. */}
      <div style={{ display: "none", fontSize: fs(12), color: B.textMuted }} className="print-only">{RANGES.find(r => r.k === dash.range)?.l}{focusText ? ` · Focus: ${focusText}` : ""}</div>

      <div data-noprint>
        <FocusBar focus={dash.focus} onChange={(focus) => onChange({ ...dash, focus })} ui={ui} />
      </div>

      {/* One filter row above everything it scopes. */}
      <div data-noprint style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
        <span style={{ fontSize: fs(11), color: B.textDim, marginRight: 4 }}>Date range</span>
        {RANGES.map(r => <button key={r.k} onClick={() => onChange({ ...dash, range: r.k })} style={pill(dash.range === r.k)}>{r.l}</button>)}
        <span data-noprint style={{ marginLeft: "auto", fontSize: fs(11), color: B.textDim }}>
          Refreshes every 5 min · last {ago(lastRefresh)} ·{" "}
          <button onClick={() => { setRefreshKey(k => k + 1); setLastRefresh(new Date().toISOString()); }} style={{ background: "none", border: "none", color: B.accent, cursor: "pointer", fontSize: fs(11), padding: 0, fontFamily: "'Satoshi', sans-serif" }}>Refresh now</button>
        </span>
      </div>
      {status && <div data-noprint style={{ ...card(ui), padding: "10px 14px", fontSize: fs(12), color: B.textMuted, marginBottom: 14, wordBreak: "break-all" }}>{status}</div>}
      {readOnly && <div data-noprint style={{ ...card(ui), padding: "10px 14px", fontSize: fs(12), color: B.textMuted, marginBottom: 14 }}>You're viewing a shared dashboard. Changes to the focus or range aren't saved — save a copy to keep it.</div>}

      {dash.widgets.length === 0 ? (
        <div style={{ ...card(ui), padding: 30, textAlign: "center", color: B.textMuted, fontSize: fs(13) }}>
          This dashboard is empty. <button onClick={() => { setEditing(true); setEditor("new"); }} style={{ background: "none", border: "none", color: B.accent, cursor: "pointer", fontSize: fs(13), fontFamily: "'Satoshi', sans-serif" }}>Add your first widget →</button>
        </div>
      ) : (
        <DashboardGrid dash={dash} schema={schema} ui={ui} editing={editing && !readOnly} refreshKey={refreshKey} onChange={onChange} onEditWidget={(w) => setEditor(w)} onDrill={setDrill} />
      )}

      {drill && <DrillDrawer request={drill} ui={ui} onClose={() => setDrill(null)} />}
      {editor && <WidgetEditor initial={editor === "new" ? null : editor} schema={schema} range={dash.range} focus={dash.focus} ui={ui} onSave={saveWidget} onCancel={() => setEditor(null)} />}
    </div>
  );
}

function DashboardList({ dashboards, ui, onCreate, onImport }) {
  const { B, fs, HP } = ui;
  const fileRef = useRef(null);
  const [error, setError] = useState("");
  const tags = [...new Set(TEMPLATES.map(t => t.tag))];

  const importFile = async (file) => {
    setError("");
    if (!file) return;
    if (file.size > 512 * 1024) { setError("That file is too large to be a dashboard."); return; }
    try {
      const parsed = JSON.parse(await file.text());
      const d = sanitize(parsed);
      if (!d || (parsed.format && parsed.format !== FILE_FORMAT)) throw new Error("not a dashboard file");
      onImport(d);
    } catch {
      setError("Couldn't read that file. Pick a .dashboard.json file exported from Idle Hour.");
    }
  };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 6 }}>
        <h1 style={{ fontSize: fs(28), fontWeight: 900, margin: 0, color: B.text, flex: 1 }}>Dashboards</h1>
        <button onClick={() => fileRef.current?.click()} style={{ ...HP.navBtn, padding: "8px 14px" }}>Import file</button>
        <input ref={fileRef} type="file" accept=".json,application/json" style={{ display: "none" }} onChange={e => { importFile(e.target.files?.[0]); e.target.value = ""; }} />
        <button onClick={() => onCreate(null)} style={{ ...HP.primaryBtn, padding: "9px 16px" }}>+ New dashboard</button>
      </div>
      <p style={{ color: B.textMuted, fontSize: fs(13), margin: "0 0 20px", lineHeight: 1.6, maxWidth: "110ch" }}>
        Build your own views of the Knowledge Base data — ransomware victims, feed indicators and ATT&CK usage. Use the Focus bar on any dashboard to narrow every widget to the actors, malware, countries or sectors you care about. Widgets refresh themselves as new data comes in. Dashboards are saved in this browser; Share or Export to move them elsewhere.
      </p>
      {error && <div style={{ ...card(ui), padding: "10px 14px", fontSize: fs(12), color: "#dc2626", marginBottom: 14 }}>{error}</div>}

      {dashboards.length > 0 && (
        <>
          <div style={{ ...sectionLabel(ui), marginBottom: 10 }}>Your dashboards</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 280px), 1fr))", gap: 12, marginBottom: 28 }}>
            {dashboards.map(d => {
              const focus = [...(d.focus?.threats || []), ...(d.focus?.countries || []), ...(d.focus?.sectors || [])].map(x => x.label);
              return (
                <a key={d.id} href={`#/dash/${encodeURIComponent(d.id)}`} style={{ ...card(ui), padding: 16, textDecoration: "none", display: "flex", flexDirection: "column", gap: 6 }}>
                  <span style={{ fontSize: fs(15), fontWeight: 800, color: B.text }}>{d.name}</span>
                  {focus.length > 0 && <span style={{ fontSize: fs(12), color: B.textMuted }}>Focus: {focus.slice(0, 4).join(", ")}{focus.length > 4 ? ` +${focus.length - 4}` : ""}</span>}
                  <span style={{ fontSize: fs(12), color: B.textDim }}>{d.widgets.length} widget{d.widgets.length === 1 ? "" : "s"} · edited {ago(d.updatedAt)}</span>
                </a>
              );
            })}
          </div>
        </>
      )}

      {tags.map(tag => (
        <div key={tag} style={{ marginBottom: 22 }}>
          <div style={{ ...sectionLabel(ui), marginBottom: 10 }}>Templates · {tag}</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(min(100%, 300px), 1fr))", gap: 12 }}>
            {TEMPLATES.filter(t => t.tag === tag).map(t => (
              <button key={t.key} onClick={() => onCreate(t)} style={{ ...card(ui), padding: 16, textAlign: "left", cursor: "pointer", display: "flex", flexDirection: "column", gap: 6, fontFamily: "'Satoshi', sans-serif" }}>
                <span style={{ fontSize: fs(15), fontWeight: 800, color: B.text }}>{t.name}</span>
                <span style={{ fontSize: fs(12), color: B.textMuted, lineHeight: 1.5 }}>{t.blurb}</span>
                {t.focus && <span style={{ fontSize: fs(11), color: B.textDim }}>Starts focused on {[...(t.focus.threats || []), ...(t.focus.countries || []), ...(t.focus.sectors || [])].map(x => x.label).join(", ")} — change it anytime</span>}
                <span style={{ fontSize: fs(11), color: B.accent, marginTop: 4 }}>{t.widgets.length} widgets · Use template →</span>
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Page ───
export function DashboardsPage({ onNavigate, user, onLogin, onLogout, pcapSession, collapsed, onToggleCollapse, ui }) {
  const { B, fs, AppShell } = ui;
  const [route, setRoute] = useState(() => parseDashHash() || { view: "list" });
  const [dashboards, setDashboards] = useState(loadAll);
  const [storageOk, setStorageOk] = useState(true);
  const schemaQ = useJson("/api/dash/schema");
  const schema = schemaQ.data?.datasets;

  const onHash = useCallback(() => { const r = parseDashHash(); if (r) setRoute(r); }, []);
  useEffect(() => {
    if (!parseDashHash()) window.history.replaceState(null, "", "#/dash");
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [onHash]);

  const persist = (list) => { setDashboards(list); setStorageOk(saveAll(list)); };
  const update = (d) => persist(dashboards.map(x => (x.id === d.id ? { ...d, updatedAt: new Date().toISOString() } : x)));
  const create = (template, nameOverride) => {
    const d = {
      id: newId(), name: nameOverride || template?.name || "Untitled dashboard", range: template?.range || "30d",
      focus: { ...EMPTY_FOCUS, ...(template?.focus || {}) },
      widgets: (template?.widgets || []).map(w => ({ ...w, id: newId() })), updatedAt: new Date().toISOString(),
    };
    persist([d, ...dashboards]);
    window.location.hash = `#/dash/${encodeURIComponent(d.id)}`;
  };

  // A shared dashboard lives only in local state: range and focus can change, nothing is saved.
  const [shared, setShared] = useState(null);
  useEffect(() => { setShared(route.view === "shared" ? decodeDashboard(route.data) : null); }, [route]);
  const current = route.view === "dashboard" ? dashboards.find(d => d.id === route.id) : null;
  const withFocus = (d) => (d ? { ...d, focus: { ...EMPTY_FOCUS, ...(d.focus || {}) } } : d);

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden" }}>
      <style>{printCss(B.bg)}</style>
      <AppShell page="dashboards" onNavigate={onNavigate} user={user} onLogin={onLogin} onLogout={onLogout} pcapSession={pcapSession} collapsed={collapsed} onToggleCollapse={onToggleCollapse} />
      <div style={{ flex: 1, minWidth: 0, overflowY: "auto", overflowX: "hidden", background: B.bg, color: B.text, fontFamily: "'Satoshi', sans-serif", padding: "28px clamp(16px, 3vw, 40px) 60px" }}>
        <div style={{ width: "100%", maxWidth: 2200, margin: "0 auto" }}>
          {!storageOk && <div style={{ ...card(ui), padding: "10px 14px", fontSize: fs(12), color: "#dc2626", marginBottom: 14 }}>This browser isn't letting the site save data, so dashboards will be lost when you leave. Use Share or Export to keep them.</div>}
          {schemaQ.error && <div style={{ color: "#dc2626", fontSize: fs(12) }}>{schemaQ.error}</div>}
          {!schema && !schemaQ.error && <div style={{ color: B.textDim, fontSize: fs(13) }}>Loading…</div>}
          {schema && route.view === "list" && <DashboardList dashboards={dashboards} ui={ui} onCreate={create} onImport={(d) => create(d, d.name)} />}
          {schema && route.view === "dashboard" && (current
            ? <DashboardView key={current.id} dash={withFocus(current)} schema={schema} ui={ui} onChange={update}
                onDelete={() => { persist(dashboards.filter(d => d.id !== current.id)); window.location.hash = "#/dash"; }}
                onDuplicate={() => create({ ...current, name: `${current.name} (copy)` })} />
            : <div style={{ ...card(ui), padding: 20, color: B.textMuted, fontSize: fs(13) }}>This dashboard isn't saved in this browser. <a href="#/dash" style={{ color: B.accent }}>Back to dashboards</a></div>)}
          {schema && route.view === "shared" && (shared
            ? <DashboardView dash={shared} schema={schema} ui={ui} readOnly onChange={setShared} onSaveCopy={() => create(shared, shared.name)} />
            : <div style={{ ...card(ui), padding: 20, color: B.textMuted, fontSize: fs(13) }}>This share link is broken or incomplete. <a href="#/dash" style={{ color: B.accent }}>Back to dashboards</a></div>)}
        </div>
      </div>
    </div>
  );
}
